const HEADING_SELECTOR = "h1, h2, h3";
const SLUG_MAX_LENGTH = 80;

export const CONTENTS_MIN_HEADINGS = 3;

let nextExplanationContentsInstanceId = 1;

export const createExplanationContentsIdPrefix = () =>
  `explanation-${nextExplanationContentsInstanceId++}`;

export const normalizeHeadingText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

export const buildHeadingSlug = (value) => {
  const slug = normalizeHeadingText(value)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");

  return slug || "section";
};

export const getHeadingLevel = (tagName) => {
  const match = /^h([1-3])$/i.exec(String(tagName || ""));
  return match ? Number(match[1]) : 2;
};

export const selectExplanationContentsHeadings = (headings) => {
  const normalized = (Array.isArray(headings) ? headings : [])
    .map((heading) => ({
      ...heading,
      text: normalizeHeadingText(heading?.text),
      level: getHeadingLevel(heading?.tagName),
    }))
    .filter((heading) => heading.text);

  return normalized;
};

// Maps each heading level present to a render-indent depth by ranking the
// distinct levels in ascending order. {2,3} -> {2:0, 3:1}; an orphan H3 alone
// -> {3:0}. Naturally clamps to the count of distinct levels (max 2).
export const buildHeadingDepthMap = (levels) => {
  const safeLevels = Array.isArray(levels) ? levels : [];
  const uniqueSorted = [...new Set(safeLevels)].sort((a, b) => a - b);
  const depthByLevel = new Map();
  uniqueSorted.forEach((level, index) => depthByLevel.set(level, index));
  return depthByLevel;
};

export const pickActiveContentsId = (orderedIds, visibleIds, previousId) => {
  const ids = Array.isArray(orderedIds)
    ? orderedIds.filter((id) => typeof id === "string" && id)
    : [];
  if (!ids.length) return null;

  const visible =
    visibleIds instanceof Set
      ? visibleIds
      : new Set(Array.isArray(visibleIds) ? visibleIds : []);
  const firstVisibleId = ids.find((id) => visible.has(id));
  if (firstVisibleId) return firstVisibleId;

  if (previousId && ids.includes(previousId)) return previousId;

  return ids[0];
};

// Builds contents items from ALREADY-SELECTED headings. Does not re-select.
export const buildExplanationContentsItems = (selectedHeadings, idPrefix) => {
  const safePrefix = normalizeHeadingText(idPrefix) || "explanation";
  const prepared = (Array.isArray(selectedHeadings) ? selectedHeadings : [])
    .map((heading) => ({
      text: normalizeHeadingText(heading?.text),
      level: getHeadingLevel(heading?.tagName),
    }))
    .filter((heading) => heading.text);

  const depthByLevel = buildHeadingDepthMap(
    prepared.map((heading) => heading.level),
  );
  const seenSlugs = new Map();
  const items = [];

  for (const heading of prepared) {
    const baseSlug = buildHeadingSlug(heading.text);
    const count = (seenSlugs.get(baseSlug) || 0) + 1;
    seenSlugs.set(baseSlug, count);

    items.push({
      id: `${safePrefix}-${count === 1 ? baseSlug : `${baseSlug}-${count}`}`,
      text: heading.text,
      level: heading.level,
      depth: depthByLevel.get(heading.level) ?? 0,
    });
  }

  return items;
};

export const buildExplanationContents = (html, options = {}) => {
  const sourceHtml = typeof html === "string" ? html : "";
  const idPrefix =
    typeof options.idPrefix === "string" && options.idPrefix.trim()
      ? options.idPrefix.trim()
      : createExplanationContentsIdPrefix();

  if (!sourceHtml.trim() || typeof DOMParser === "undefined") {
    return { html: sourceHtml, items: [] };
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(sourceHtml, "text/html");
  const headingElements = Array.from(
    document.body.querySelectorAll(HEADING_SELECTOR),
  );
  const selectedHeadings = selectExplanationContentsHeadings(
    headingElements.map((element) => ({
      element,
      text: element.textContent || "",
      tagName: element.tagName,
    })),
  );
  const items = buildExplanationContentsItems(selectedHeadings, idPrefix);

  selectedHeadings.forEach((heading, index) => {
    const item = items[index];
    if (item && heading.element) {
      heading.element.setAttribute("id", item.id);
    }
  });

  return { html: document.body.innerHTML, items };
};
